// Built / Lit network view. Read-mostly. Shows every element whose status is
// already built, as-built, or in_service — across every plant. Each element
// can be toggled to in_service ("Lit") once construction + cutover are done.

import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { BASEMAPS, type BasemapId } from '../lib/map-basemaps';
import { nodeCirclePaint, segmentPaint } from '../lib/plant-styles';
import ErrorBoundary from '../components/ErrorBoundary';
import type { NodeFeature, PlantGraph, SegmentFeature } from '../types/plant';

const WV_CENTER: [number, number] = [-80.45, 38.6];

type SelectedBuilt =
  | { kind: 'node'; feature: NodeFeature }
  | { kind: 'segment'; feature: SegmentFeature }
  | null;

export default function BuiltPageWrapped() {
  return <ErrorBoundary><BuiltPageInner /></ErrorBoundary>;
}

function BuiltPageInner() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const qc = useQueryClient();
  const [basemap, setBasemap] = useState<BasemapId>('satellite');
  const [statusFilter, setStatusFilter] = useState<'all' | 'built' | 'lit'>('all');
  const [selected, setSelected] = useState<SelectedBuilt>(null);
  const [mapReady, setMapReady] = useState(false);
  // Multi-select for bulk actions. Shift-click a segment or node to toggle.
  const [multiSelected, setMultiSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Fetch the cross-plant "built network" — served by a dedicated backend
  // endpoint so we don't have to orchestrate per-plant calls.
  const { data: graph, refetch } = useQuery<PlantGraph>({
    queryKey: ['built-network', statusFilter],
    queryFn: () => api.get(`/built?filter=${statusFilter}`),
  });

  const litMut = useMutation({
    mutationFn: (args: { kind: 'node' | 'segment'; id: string; status: string }) =>
      api.post(`/${args.kind === 'node' ? 'nodes' : 'segments'}/${args.id}/status`, { status: args.status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['built-network'] });
      refetch();
    },
  });

  // ---- Map init ----
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: BASEMAPS[basemap].style,
      center: WV_CENTER,
      zoom: 7,
      hash: false,
    });
    map.addControl(new maplibregl.NavigationControl({}), 'top-right');
    map.addControl(new maplibregl.ScaleControl({ unit: 'imperial', maxWidth: 120 }));
    map.on('load', () => {
      // Segments (colored by status: in_service=bright green, built=blue, as_built=teal)
      map.addSource('built-segments', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
      map.addLayer({
        id: 'built-segments-casing', type: 'line', source: 'built-segments',
        paint: { 'line-color': '#ffffff', 'line-width': 6, 'line-opacity': 0.55 },
        layout: { 'line-cap': 'round', 'line-join': 'round' },
      });
      map.addLayer({
        id: 'built-segments', type: 'line', source: 'built-segments',
        paint: {
          'line-color': ['match', ['get', 'status'],
            'in_service', '#10b981',
            'as_built',   '#14b8a6',
            'built',      '#3b82f6',
            '#94a3b8'] as any,
          'line-width': ['match', ['get', 'type'],
            'feeder_trunk', 5,
            'distribution', 3,
            'drop_tail',    1.5,
            2] as any,
          'line-opacity': 0.95,
        },
        layout: { 'line-cap': 'round', 'line-join': 'round' },
      });
      map.addSource('built-nodes', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
      map.addLayer({
        id: 'built-nodes', type: 'circle', source: 'built-nodes',
        paint: nodeCirclePaint() as any,
      });
      mapRef.current = map;
      setMapReady(true);
    });
    return () => { map.remove(); mapRef.current = null; setMapReady(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Swap basemap
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    map.setStyle(BASEMAPS[basemap].style);
    map.once('styledata', () => {
      // re-add our layers
      if (!map.getSource('built-segments')) {
        map.addSource('built-segments', { type: 'geojson', data: graph?.segments ?? ({ type: 'FeatureCollection', features: [] } as any) } as any);
        map.addLayer({ id: 'built-segments-casing', type: 'line', source: 'built-segments',
          paint: { 'line-color': '#ffffff', 'line-width': 6, 'line-opacity': 0.55 },
          layout: { 'line-cap': 'round', 'line-join': 'round' } });
        map.addLayer({ id: 'built-segments', type: 'line', source: 'built-segments', paint: segmentPaint() as any });
      }
      if (!map.getSource('built-nodes')) {
        map.addSource('built-nodes', { type: 'geojson', data: graph?.nodes ?? ({ type: 'FeatureCollection', features: [] } as any) } as any);
        map.addLayer({ id: 'built-nodes', type: 'circle', source: 'built-nodes', paint: nodeCirclePaint() as any });
      }
    });
  }, [basemap]);

  // Push data to map. Depends on BOTH graph AND mapReady — graph may arrive
  // before the map finishes loading (cached query etc.); we re-run when map
  // becomes ready to catch that case.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map || !map.getSource('built-segments')) return;
    (map.getSource('built-segments') as maplibregl.GeoJSONSource).setData(graph?.segments ?? ({ type: 'FeatureCollection', features: [] } as any));
    (map.getSource('built-nodes')    as maplibregl.GeoJSONSource).setData(graph?.nodes    ?? ({ type: 'FeatureCollection', features: [] } as any));
    if (graph && (graph.nodes.features.length || graph.segments.features.length)) {
      const b = new maplibregl.LngLatBounds();
      for (const f of graph.nodes.features) {
        const c = f.geometry?.coordinates;
        if (Array.isArray(c) && typeof c[0] === 'number') b.extend(c as [number, number]);
      }
      for (const f of graph.segments.features) {
        const cs = f.geometry?.coordinates;
        if (Array.isArray(cs)) for (const c of cs) if (Array.isArray(c) && typeof c[0] === 'number') b.extend(c as [number, number]);
      }
      if (!b.isEmpty()) map.fitBounds(b, { padding: 80, duration: 0, maxZoom: 16 });
    }
  }, [graph, mapReady]);

  // Click handler for selection. Plain click = select one. Shift-click =
  // toggle into the multi-select bag.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const onClick = (e: maplibregl.MapMouseEvent & { originalEvent: MouseEvent }) => {
      const hits = map.queryRenderedFeatures(e.point, { layers: ['built-nodes','built-segments'].filter((id) => map.getLayer(id)) });
      const n = hits.find((h) => h.layer.id === 'built-nodes');
      const s = hits.find((h) => h.layer.id === 'built-segments');
      const hit = n ?? s;
      const shift = e.originalEvent?.shiftKey;
      if (shift && hit) {
        const id = (hit as any).id ?? (hit.properties as any)?.id;
        if (!id) return;
        setMultiSelected((prev) => {
          const next = new Set(prev);
          const key = String(id);
          if (next.has(key)) next.delete(key); else next.add(key);
          return next;
        });
        return;
      }
      if (n)      setSelected({ kind: 'node',    feature: n as any });
      else if (s) setSelected({ kind: 'segment', feature: s as any });
      else        setSelected(null);
    };
    map.on('click', onClick);
    return () => { map.off('click', onClick); };
  }, []);

  // Highlight multi-selected segments in the map
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer('built-segments')) return;
    // Stash on a feature-state so paint can react; but simpler: toggle
    // line-width via filter expressions. We just pipe it to a dedicated layer.
    // For now we add a quick visual: thicker stroke on selected.
    // (Hacky but works: use filter to highlight.)
    const ids = Array.from(multiSelected);
    const layerId = 'built-segments-multisel';
    if (!map.getLayer(layerId)) {
      map.addLayer({
        id: layerId, type: 'line', source: 'built-segments',
        paint: { 'line-color': '#0ea5e9', 'line-width': 10, 'line-opacity': 0.4 },
        filter: ['in', ['id'], ['literal', ids]] as any,
      });
    } else {
      map.setFilter(layerId, ['in', ['id'], ['literal', ids]] as any);
    }
  }, [multiSelected]);

  async function handleLightSelected() {
    if (multiSelected.size === 0) return;
    // Only segment ids (not node ids) go through bulk-status. We lump them
    // via a simple heuristic: nodes are shorter strings? No — both UUIDs.
    // Instead, we'll accept whatever is in the set and let the backend skip
    // non-segment ids by ignoring the UUID mismatches.
    setBulkBusy(true);
    try {
      const r = await api.post('/segments/bulk-status', {
        segment_ids: Array.from(multiSelected),
        status: 'in_service',
      });
      alert(`Lit ${r.changed} segments${r.overbuild_count ? ` · ${r.overbuild_count} OVERBUILD WARNINGS` : ''}`);
      setMultiSelected(new Set());
      refetch();
    } catch (e) {
      alert('Bulk light failed: ' + (e as Error).message);
    } finally {
      setBulkBusy(false);
    }
  }

  async function handleLightDownstream() {
    if (!selected || selected.kind !== 'node') return;
    const fid = (selected.feature as any).id ?? (selected.feature.properties as any)?.id;
    if (!fid) { alert('No node id on selected feature.'); return; }
    setBulkBusy(true);
    try {
      const r = await api.post(`/nodes/${fid}/light-downstream`, {});
      alert(`Lit ${r.lit_count} downstream segments${r.overbuild_count ? ` · ${r.overbuild_count} overbuild warnings` : ''}`);
      refetch();
    } catch (e) {
      alert('Light-downstream failed: ' + (e as Error).message);
    } finally {
      setBulkBusy(false);
    }
  }

  const counts = graph
    ? {
        segments: graph.segments.features.length,
        nodes: graph.nodes.features.length,
        lit: graph.segments.features.filter((f) => f.properties.status === 'in_service').length,
        built: graph.segments.features.filter((f) => f.properties.status === 'built' || f.properties.status === 'as_built').length,
      }
    : { segments: 0, nodes: 0, lit: 0, built: 0 };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr 320px', gap: 16, height: 'calc(100vh - var(--topbar-h) - 48px)' }}>
      <div className="card" style={{ height: '100%', overflow: 'auto' }}>
        <div className="card__header">Filters</div>
        <div className="card__body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div className="panel-label">Show</div>
            <div className="map-seg" style={{ width: '100%' }}>
              {(['all','built','lit'] as const).map((f) => (
                <button key={f}
                        className={`map-seg__btn ${statusFilter === f ? 'active' : ''}`}
                        style={{ flex: 1 }}
                        onClick={() => setStatusFilter(f)}>
                  {f === 'all' ? 'All' : f === 'built' ? 'Built only' : 'Lit only'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="panel-label">Basemap</div>
            <div className="map-seg" style={{ width: '100%' }}>
              {(['satellite','streets','topo'] as const).map((b) => (
                <button key={b} className={`map-seg__btn ${basemap === b ? 'active' : ''}`}
                        style={{ flex: 1 }} onClick={() => setBasemap(b)}>
                  {b}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="panel-label">Network stats</div>
            <div className="plant-stats">
              <div className="plant-stat"><div className="plant-stat__label">Nodes</div><div className="plant-stat__value">{counts.nodes}</div></div>
              <div className="plant-stat"><div className="plant-stat__label">Segments</div><div className="plant-stat__value">{counts.segments}</div></div>
              <div className="plant-stat"><div className="plant-stat__label">Lit</div><div className="plant-stat__value" style={{ color: '#10b981' }}>{counts.lit}</div></div>
              <div className="plant-stat"><div className="plant-stat__label">Built</div><div className="plant-stat__value" style={{ color: '#3b82f6' }}>{counts.built}</div></div>
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
            <b>Legend:</b>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
              <span style={{ width: 16, height: 3, background: '#10b981', display: 'inline-block' }} /> Lit / in service
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
              <span style={{ width: 16, height: 3, background: '#14b8a6', display: 'inline-block' }} /> As-built
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
              <span style={{ width: 16, height: 3, background: '#3b82f6', display: 'inline-block' }} /> Built
            </div>
          </div>
        </div>
      </div>

      <div className="map-wrap" style={{ height: '100%', position: 'relative' }}>
        <div ref={containerRef} style={{ height: '100%', width: '100%' }} />
        <div className="map-statbar">
          {counts.segments} segments · {counts.lit} lit / {counts.built} built
          <span style={{ marginLeft: 12, opacity: 0.6 }}>Shift+Click to multi-select</span>
        </div>
        {multiSelected.size > 0 && (
          <div className="map-banner map-banner--info" style={{ top: 12 }}>
            <span><b>{multiSelected.size}</b> selected</span>
            <button className="btn btn--primary" onClick={handleLightSelected} disabled={bulkBusy} style={{ marginLeft: 12 }}>
              💡 {bulkBusy ? 'Lighting…' : `Light ${multiSelected.size}`}
            </button>
            <button className="btn" onClick={() => setMultiSelected(new Set())} style={{ marginLeft: 6 }}>Clear</button>
          </div>
        )}
      </div>

      <div className="card" style={{ height: '100%', overflow: 'auto' }}>
        <div className="card__header">Inspector</div>
        <div className="card__body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!selected && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Click a segment or node on the map. You can change its status here.
            </div>
          )}
          {selected && (
            <>
              <div style={{ fontSize: 13 }}>
                <b>{selected.kind === 'node' ? 'Node' : 'Segment'}:</b> <code>{(selected.feature.properties as any).auto_label}</code>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Current status: <b>{(selected.feature.properties as any).status}</b>
              </div>
              {selected.kind === 'node' && (
                <button
                  className="btn btn--primary"
                  onClick={handleLightDownstream}
                  disabled={bulkBusy}
                  style={{ justifyContent: 'center', marginBottom: 8 }}
                  title="Walk the tree from this node and light every reachable planned/built segment"
                >
                  💡 Light everything downstream
                </button>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {['built','as_built','in_service','retired'].map((s) => (
                  <button key={s} className="btn"
                          onClick={() => {
                            const fid = (selected.feature as any).id ?? (selected.feature.properties as any)?.id;
                            if (!fid) return;
                            litMut.mutate({ kind: selected.kind, id: fid as string, status: s });
                          }}
                          disabled={litMut.isPending}
                          style={{
                            justifyContent: 'flex-start',
                            background: s === 'in_service' ? '#d1fae5' : undefined,
                            borderColor:  s === 'in_service' ? '#10b981' : undefined,
                          }}>
                    {s === 'in_service' ? '💡 ' : ''}Set status → {s.replace('_',' ')}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
